import { useParams } from 'react-router-dom';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useInjectedMeta } from '../lib/useInjectedMeta';
import { AppCta } from '../components/AppCta';
import { sanitizeShortCode, skiffAppLink } from '../lib/appLinks';

/**
 * Public skiff share landing — `/s/:code`. The PB hook share_skiff_html.pb.js
 * serves OG tags for crawlers; this React component renders the human-facing
 * landing page after the SPA hydrates.
 *
 * The goal statement is read back out of the injected `<head>` rather than
 * fetched — the hook has already resolved it server-side, so there is no need
 * for the anonymous preview endpoint this file's earlier comment anticipated.
 */
export function SkiffShare() {
  const { code } = useParams<{ code: string }>();
  const injectedTitle = useInjectedMeta('og:title');
  const detail = useInjectedMeta('og:description');

  // "You're invited" is wrong for a skiff that has already finished — you
  // cannot join a landed goal. The hook signals which case this is by
  // prefixing the title, so the page can stop inviting people to something
  // that is over.
  const landed = injectedTitle?.startsWith('Landed on Skiffs') ?? false;

  useDocumentTitle(landed ? 'A goal landed on Skiffs' : 'Skiffs invite');

  const sanitizedCode = sanitizeShortCode(code);
  const deepLink = skiffAppLink(code);

  return (
    <article className="skf-card">
      <h1 className="skf-title">{landed ? 'A goal, landed.' : <>You&rsquo;re invited.</>}</h1>
      <p className="skf-subtitle">
        {detail ??
          (sanitizedCode ? `Skiff code: ${sanitizedCode}` : 'Open Skiffs to view this skiff.')}
      </p>
      <AppCta deepLink={deepLink} />
    </article>
  );
}
